import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ArrowLeft, UserPlus, Zap, Flame, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FamilyDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user?.plan !== 'famille') {
      navigate('/pricing');
      return;
    }
    loadFamily();
  }, [user, navigate]);

  const loadFamily = async () => {
    try {
      const { data } = await axios.get(`${API}/family`, { withCredentials: true });
      setFamily(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setError('');
    setSuccess('');
    setInviting(true);
    try {
      await axios.post(`${API}/family/invite`, { email: inviteEmail }, { withCredentials: true });
      setSuccess(`Invitation envoyée à ${inviteEmail}`);
      setInviteEmail('');
      loadFamily();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'invitation');
    } finally {
      setInviting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-4 space-y-6">
        <div className="flex items-center gap-3 animate-fade-up">
          <button onClick={() => navigate('/profile')} className="w-9 h-9 bg-[#1a1a24] rounded-xl flex items-center justify-center border border-white/10" data-testid="back-btn">
            <ArrowLeft size={16} className="text-white" />
          </button>
          <div>
            <h1 className="font-heading text-xl font-bold text-white">Tableau de bord Famille</h1>
            <p className="text-[#e8e8e8] text-sm">Gérez les membres de votre famille</p>
          </div>
        </div>

        {/* Invite form */}
        <div className="kurso-card p-5 animate-fade-up" style={{ animationDelay: '0.1s' }}>
          <h2 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
            <UserPlus size={16} className="text-[#20c997]" /> Inviter un membre
          </h2>
          <form onSubmit={handleInvite} className="flex gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="Email du membre"
              className="flex-1 bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#20c997]/50"
              data-testid="invite-email-input"
            />
            <button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              className="btn-primary px-4 py-2.5 text-sm disabled:opacity-50"
              data-testid="invite-submit-btn"
            >
              {inviting ? <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" /> : 'Inviter'}
            </button>
          </form>
          {error && <p className="text-[#ff6b6b] text-xs mt-2">{error}</p>}
          {success && <p className="text-[#20c997] text-xs mt-2">{success}</p>}
        </div>

        {/* Members */}
        <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
          <h2 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
            <Users size={16} className="text-[#ff6b6b]" /> Membres ({family?.members_data?.length || 0}/5)
          </h2>
          {loading ? (
            [1, 2, 3].map(i => <div key={i} className="animate-shimmer h-16 rounded-2xl mb-3" />)
          ) : family?.members_data?.length === 0 ? (
            <div className="kurso-card p-6 text-center">
              <Users size={28} className="text-[#e8e8e8] mx-auto mb-2" />
              <p className="text-[#e8e8e8] text-sm">Aucun membre pour l'instant. Invitez votre famille !</p>
            </div>
          ) : (
            <div className="space-y-3">
              {family.members_data.map((member, i) => (
                <div key={member.id} className="kurso-card p-4 flex items-center gap-4" data-testid={`member-${member.id}`}>
                  <div className="w-10 h-10 rounded-2xl bg-[#ff6b6b]/10 flex items-center justify-center">
                    <span className="font-heading font-bold text-[#ff6b6b]">{member.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium text-sm">{member.name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1 text-xs text-[#e8e8e8]"><Zap size={10} className="text-[#ffc107]" /> {member.xp} XP</span>
                      <span className="flex items-center gap-1 text-xs text-[#e8e8e8]"><Flame size={10} className="text-[#ff6b6b]" /> {member.streak}j</span>
                      <span className="flex items-center gap-1 text-xs text-[#e8e8e8]"><BookOpen size={10} /> {member.badges?.length || 0} badges</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
